/* ============================================================
 * frontend-v3/src/screens/settings/YoutubeAccounts.tsx
 *
 * "Cuentas de YouTube" — the manage-accounts surface reached from the
 * Destinos account picker (channel settings → Destinos).
 *
 * Before this screen there was NO way to add a YouTube account from inside
 * CIRCA: the Destinos picker could only choose among connections someone had
 * created by hand in the Nango dashboard, and `deleteNangoConnection` existed
 * in the library with nothing calling it.
 *
 * Adding an account is an OAuth consent flow, so it cannot happen in-app: we
 * create a Nango Connect session server-side and send the operator to Nango's
 * hosted consent page. Nango then fires its connection-created webhook, which
 * `convex/nangoWebhooks.ts` already consumes — so this screen never writes a
 * connection, and nothing exists until consent actually completes. That is why
 * the list refreshes on return rather than optimistically inserting a row.
 *
 * Honest-state (CLAUDE.md §5): Skeleton while loading, EmptyState when there
 * are no accounts, real backend errors surface as error pills / toasts. Usage
 * counts come from the real channel list, so an account in use says so.
 * ============================================================ */
import { useCallback, useEffect, useState } from 'react';
import { useAction, useQuery } from 'convex/react';
import { api } from '@convex/api';
import { toast } from 'sonner';
import {
  Btn,
  ConfirmModal,
  EmptyState,
  PageHead,
  Panel,
  Pill,
  Skeleton,
} from '../../components/primitives';

interface ConnectionRow {
  connectionId: string;
  label: string;
  youtubeTitle: string | null;
  email: string | null;
  createdAt: string | null;
}

interface ChannelRow {
  _id: string;
  name: string;
  nangoYoutubeConnectionId?: string;
  archivedAt?: number;
}

export function YoutubeAccounts() {
  const listConnections = useAction(
    api.actions.youtubeConnections.listYoutubeConnections,
  );
  const createSession = useAction(
    api.actions.youtubeConnections.createYoutubeConnectSession,
  );
  const deleteConnection = useAction(
    api.actions.youtubeConnections.deleteYoutubeConnection,
  );

  const channels = useQuery(api.channels.listChannels) as
    | ChannelRow[]
    | undefined;

  const [conns, setConns] = useState<ConnectionRow[] | null | undefined>(
    undefined,
  );
  const [reload, setReload] = useState(0);
  const [busy, setBusy] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ConnectionRow | null>(null);

  useEffect(() => {
    let alive = true;
    listConnections({})
      .then((r: ConnectionRow[]) => { if (alive) setConns(r); })
      .catch(() => { if (alive) setConns(null); });
    return () => { alive = false; };
  }, [listConnections, reload]);

  // Consent completes in another tab, so the only reliable signal that a new
  // account exists is the operator coming back to this one.
  useEffect(() => {
    const onFocus = (): void => setReload((k) => k + 1);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const channelsFor = useCallback(
    (connectionId: string): ChannelRow[] =>
      (channels ?? []).filter(
        (c) => !c.archivedAt && c.nangoYoutubeConnectionId === connectionId,
      ),
    [channels],
  );

  async function onAdd(): Promise<void> {
    setBusy(true);
    try {
      const { connectUrl } = await createSession({});
      // New tab: the consent flow is Google's, and losing the console's state
      // mid-flow would strand any unsaved work behind it.
      window.open(connectUrl, '_blank', 'noopener,noreferrer');
      toast.success('Completa el permiso en la pestaña de Google');
    } catch {
      toast.error('No se pudo iniciar la vinculación de la cuenta');
    } finally {
      setBusy(false);
    }
  }

  async function onDelete(row: ConnectionRow): Promise<void> {
    setBusy(true);
    try {
      await deleteConnection({ connectionId: row.connectionId });
      toast.success('Cuenta desvinculada');
      setReload((k) => k + 1);
    } catch {
      toast.error('No se pudo desvincular la cuenta');
    } finally {
      setBusy(false);
      setPendingDelete(null);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHead
        // No href on "Ajustes": Breadcrumbs renders a raw <a>, and routing here
        // goes through the `lib/nav` shim to react-router — a `#/settings` href
        // only appends a hash fragment instead of navigating.
        crumbs={[{ label: 'Ajustes' }, { label: 'Cuentas de YouTube' }]}
        title="Cuentas de YouTube"
        description="Cuentas de Google autorizadas para leer el estado en vivo y publicar en YouTube. Una cuenta puede respaldar varios canales."
      />

      <Panel
        title="Cuentas vinculadas"
        reviewed="wired"
        right={
          <Btn
            variant="solid"
            size="sm"
            icon="plus"
            reviewed="wired"
            loading={busy}
            onClick={() => { void onAdd(); }}
          >
            Vincular cuenta
          </Btn>
        }
      >
        {conns === undefined ? (
          <Skeleton h={120} />
        ) : conns === null ? (
          <Pill tone="bad" size="sm">
            No se pudieron cargar las cuentas — verifica la conexión con Nango
          </Pill>
        ) : conns.length === 0 ? (
          <EmptyState
            icon="link"
            title="Sin cuentas vinculadas"
            body="Vincula una cuenta de Google para poder elegir transmisiones reales en los destinos de cada canal."
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {conns.map((c) => {
              const used = channelsFor(c.connectionId);
              return (
                <div
                  key={c.connectionId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    padding: '10px 0',
                    borderBottom: '1px solid var(--border-cool)',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <strong style={{ fontSize: 14 }}>{c.label}</strong>
                    <span
                      className="t-mono"
                      style={{ fontSize: 12, color: 'var(--muted-slate)', textTransform: 'none' }}
                    >
                      {c.email ?? c.connectionId}
                    </span>
                    {/* Usage is the safety rail on the delete below: unlinking
                       an account in use leaves those channels unidentifiable. */}
                    {used.length > 0 ? (
                      <Pill tone="good" size="sm" dot>
                        {used.length === 1
                          ? `En uso por ${used[0]!.name}`
                          : `En uso por ${used.length} canales`}
                      </Pill>
                    ) : (
                      <Pill tone="neutral" size="sm">Sin canales asociados</Pill>
                    )}
                  </div>
                  <Btn
                    variant="danger"
                    size="sm"
                    icon="trash"
                    reviewed="wired"
                    disabled={busy}
                    onClick={() => setPendingDelete(c)}
                  >
                    Desvincular
                  </Btn>
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      <ConfirmModal
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title="Desvincular cuenta de YouTube"
        danger
        typed="DESVINCULAR"
        confirmLabel="Desvincular"
        body={
          pendingDelete === null ? null : (
            <span>
              Se revocará el permiso de <strong>{pendingDelete.label}</strong>.
              {channelsFor(pendingDelete.connectionId).length > 0 && (
                <>
                  {' '}
                  <strong>
                    {channelsFor(pendingDelete.connectionId).length} canal(es)
                  </strong>{' '}
                  quedarán sin cuenta y no se podrán identificar en YouTube
                  (seguirán emitiendo con su clave actual).
                </>
              )}{' '}
              Volver a vincularla requiere repetir el permiso de Google.
            </span>
          )
        }
        onConfirm={() => { if (pendingDelete) void onDelete(pendingDelete); }}
      />
    </div>
  );
}

export default YoutubeAccounts;
